import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, Spinner } from "@safers/ui";
import { Modal, ModalContent, ModalHeader, ModalBody, ModalTitle } from "@safers/ui/organisms";
import { useAuthStore, selectUser, canAccessSite, canAccessMenuSite } from "@safers/auth";
import { eventService, type Event } from "@safers/api";
import { useLandscapeVideo } from "@/hooks/useIsFullscreen";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cctvLivePath, controlPath } from "@/routes/paths";
import { EmptyState } from "@/components/EmptyState";
import { EmptyIcon } from "@/components/emptyIcons";
import { EventMeasure } from "@/components/EventMeasure";
import { isCctvEventCategory, severityChip } from "@/services";
import { SEVERITY_LABELS } from "@/lib/map";
import { typeLabel, categoryLabel } from "@/lib/eventLabels";

const eventKeys = {
  detail: (id: number) => ["event", id] as const,
};

interface Props {
  eventId: number | null;
  onClose: () => void;
}

interface EventProps {
  event: Event;
}

interface InfoRowProps {
  label: string;
  value: string;
}

function formatDateTime(timestamp: string): string {
  const d = new Date(timestamp);
  if (!Number.isFinite(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function InfoRow({ label, value }: InfoRowProps) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <span className="w-16 flex-none text-[0.8125rem] text-neutral-500">{label}</span>
      <span className="min-w-0 flex-1 truncate text-[0.8125rem] font-semibold text-neutral-900">
        {value}
      </span>
    </div>
  );
}

/* ── 관제(영상 감지 등) ───────────────────────────────────────────── */

function Media({ event }: EventProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  useLandscapeVideo(boxRef, isMobile);

  if (event.video?.url) {
    return (
      <div ref={boxRef} className="overflow-hidden rounded-lg bg-black">
        <video
          src={event.video.url}
          controls
          autoPlay
          muted
          playsInline
          className="max-h-[20rem] w-full object-contain sm:max-h-[50vh]"
        />
      </div>
    );
  }
  if (event.snapshot?.url) {
    return (
      <div className="overflow-hidden rounded-lg bg-neutral-100">
        <img
          src={event.snapshot.url}
          alt={`${event.name} 스냅샷`}
          className="max-h-[20rem] w-full object-contain sm:max-h-[50vh]"
        />
      </div>
    );
  }
  return (
    <div className="flex h-[8rem] items-center justify-center rounded-lg bg-neutral-50 text-[0.8125rem] text-neutral-500">
      미디어 없음
    </div>
  );
}

function DetectionDetail({ event }: EventProps) {
  const navigate = useNavigate();
  const user = useAuthStore(selectUser);

  const siteId = event.site?.id ?? null;
  const canSite = siteId != null && canAccessSite(user, siteId);
  const canCctv =
    siteId != null &&
    isCctvEventCategory(event.category) &&
    canAccessMenuSite(user, siteId, "CCTV");

  const camStream = canCctv && event.path ? event.path : null;
  const cctvHref = siteId == null ? null : cctvLivePath({ siteId, cctvId: camStream });

  // replace — 이동하면 `?event=` 가 빠져 모달이 닫히고, 뒤로가기가 모달로 돌아오지 않는다
  const go = (path: string) => navigate(path, { replace: true });

  return (
    <div className="flex flex-col gap-3">
      <Media event={event} />
      <div className="divide-y divide-neutral-100">
        <InfoRow label="현장" value={event.site?.name ?? "—"} />
        <InfoRow label="유형" value={typeLabel(event.type)} />
        <InfoRow label="구분" value={categoryLabel(event.category)} />
        <InfoRow label="발생" value={formatDateTime(event.timestamp)} />
        {event.confidence != null && event.confidence > 0 && (
          <InfoRow label="신뢰도" value={`${Math.round(event.confidence * 100)}%`} />
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          disabled={!canSite}
          title={canSite ? undefined : "이 현장의 접근 권한이 없습니다"}
          onClick={() => siteId != null && go(controlPath(siteId))}
        >
          현장으로 이동
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!canCctv}
          title={canCctv ? undefined : "이 현장의 CCTV 권한이 없거나 영상 이벤트가 아닙니다"}
          onClick={() => cctvHref && go(cctvHref)}
        >
          CCTV에서 보기
        </Button>
      </div>
    </div>
  );
}

/* ── 보건 ─────────────────────────────────────────────────────────── */

function HealthDetail({ event }: EventProps) {
  return (
    <div className="flex flex-col gap-4">
      <EventMeasure event={event} />
      <div className="divide-y divide-neutral-100">
        <InfoRow label="현장" value={event.site?.name ?? "—"} />
        <InfoRow label="유형" value={typeLabel(event.type)} />
        <InfoRow label="장비" value={event.deviceId ?? "—"} />
        <InfoRow label="발생" value={formatDateTime(event.timestamp)} />
      </div>
    </div>
  );
}

/* ── 틀 ───────────────────────────────────────────────────────────── */

/**
 * 이벤트 상세. 알림 벨·배너·푸시·관제·보건 어디서 열든 이 모달 하나다(`MobileShell` 에 전역 마운트).
 *
 * 틀(헤더·로딩·오류)은 같고 본문만 **이벤트의 category** 로 고른다 — 연 화면이 아니라
 * 이벤트가 정한다. 보건은 측정값 표, 그 외(영상 감지·안전 센서)는 영상과 CCTV 이동.
 */
export function EventDetailModal({ eventId, onClose }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: eventKeys.detail(eventId ?? -1),
    queryFn: () => eventService.get(eventId as number),
    enabled: eventId != null,
    staleTime: Infinity,
  });
  const isHealth = data?.category === "HEALTH";

  return (
    <Modal open={eventId != null} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        aria-describedby={undefined}
        className={`max-h-[90dvh] w-[calc(100%-2rem)] overflow-y-auto ${isHealth ? "" : "sm:max-w-[48rem]"}`}
      >
        <ModalHeader className="p-4 pb-3 pr-14 sm:p-6 sm:pb-3 sm:pr-14">
          <ModalTitle className="flex min-w-0 items-center gap-2 text-base sm:text-lg">
            {data?.severity && (
              <span
                className={`flex-none rounded px-1.5 py-0.5 text-[0.75rem] font-bold ${severityChip(data.severity).bg} ${severityChip(data.severity).text}`}
              >
                {SEVERITY_LABELS[data.severity]}
              </span>
            )}
            <span className="truncate">{data?.name ?? "이벤트 상세"}</span>
          </ModalTitle>
        </ModalHeader>

        <ModalBody className="px-4 pb-5 sm:px-6 sm:pb-6">
          {isLoading && (
            <div className="flex h-[12rem] items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}

          {isError && (
            <EmptyState
              icon={EmptyIcon.event}
              title="이벤트를 불러오지 못했습니다"
              hint="삭제되었거나 접근 권한이 없을 수 있습니다."
            />
          )}

          {data && (isHealth ? <HealthDetail event={data} /> : <DetectionDetail event={data} />)}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
